import classNames from 'classnames';
import { ReactNode, useEffect, useRef } from 'react';
import { DropdownMenuOption, DropdownMenuSectionOption, DropdownMenuTextOption } from '../../types';
import DropdownMenuItem from './DropdownMenuItem';
import styles from './styles/styles.module.scss';

const isDropdownMenuTextOption = (option: DropdownMenuOption): option is DropdownMenuTextOption => 'title' in option;
const isDropdownMenuSectionOption = (option: DropdownMenuOption): option is DropdownMenuSectionOption => 'sectionHeader' in option;

type DropdownMenuItemsProps = {
  className?: string;
  children?: ReactNode;
  options: DropdownMenuOption[];
  scrollSelectedIntoView?: boolean;
  scrollToIndex?: number;
  textVariant?: 'text100' | 'text200';
};

const DropdownMenuItems = ({
  className,
  children,
  options,
  scrollSelectedIntoView,
  scrollToIndex,
  textVariant = 'text100'
}: DropdownMenuItemsProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedIndex = options.findIndex(option => isDropdownMenuTextOption(option) && option.selected);
  const scrollIndex = scrollSelectedIntoView && selectedIndex !== -1 ? selectedIndex : scrollToIndex;

  useEffect(() => {
    const container = containerRef.current;
    const scrollItem = scrollIndex === undefined ? undefined : container?.children[scrollIndex];
    if (!container || !(scrollItem instanceof HTMLElement)) {
      return;
    }
    container.scrollTop = scrollItem.offsetTop - container.offsetTop - (container.clientHeight - scrollItem.offsetHeight) / 2;
    // Only run on mount, since the menu is unmounted while it is closed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={classNames(styles.container, className)} ref={containerRef}>
      {options.map((option, index) => {
        const key = `option-${index}`;
        if (isDropdownMenuTextOption(option)) {
          return <DropdownMenuItem key={key} option={option} textVariant={textVariant} />;
        }
        if (isDropdownMenuSectionOption(option)) {
          return (
            <div key={key} className={styles.sectionOption}>
              <div key={key} className={styles.sectionDivider} />
              {option.sectionHeader ? <span className={styles.sectionHeader}>{option.sectionHeader}</span> : null}
            </div>
          );
        }
        return option;
      })}
      {children}
    </div>
  );
};

export default DropdownMenuItems;
